import React, { useState, useEffect, useMemo } from 'react';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  ShoppingCart, 
  Eye, 
  Target,
  MousePointerClick,
  Globe,
  Smartphone,
  Monitor,
  Search,
  Users,
  ArrowUp,
  ArrowDown,
  Calendar,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Loader2,
  Zap,
  BarChart3,
  PieChart,
  Activity,
  ExternalLink,
  TrendingDown as TrendDown
} from 'lucide-react';
import { LineChart, BarChart, DoughnutChart } from '../components/charts';
import { useAuth } from '../context/AuthContext';

// ============================================================================
// OVERVIEW V2 - Clean Dashboard with Working APIs
// ============================================================================

export default function Overviewv21738() {
  const { user } = useAuth();
  
  // -------------------------------------------------------------------------
  // STATE
  // -------------------------------------------------------------------------
  const [dateRange, setDateRange] = useState('last7');
  const [isLoading, setIsLoading] = useState(true);
  const [logoUrl, setLogoUrl] = useState('');
  
  // Google Ads data
  const [googleAdsData, setGoogleAdsData] = useState([]);
  const [isLoadingGoogle, setIsLoadingGoogle] = useState(false);
  
  // Meta Ads data
  const [metaAdsData, setMetaAdsData] = useState([]);
  const [isLoadingMeta, setIsLoadingMeta] = useState(false);
  
  // GA Traffic Source data
  const [trafficData, setTrafficData] = useState(null);
  const [isLoadingTraffic, setIsLoadingTraffic] = useState(false);
  
  // Live today data
  const [liveGads, setLiveGads] = useState(null);
  const [liveMeta, setLiveMeta] = useState(null);
  const [liveGa4, setLiveGa4] = useState(null);
  const [loadingLive, setLoadingLive] = useState(false);

  // -------------------------------------------------------------------------
  // HELPERS
  // -------------------------------------------------------------------------
  
  const getBrandId = () => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('currentBrandId');
    }
    return null;
  };

  const formatCurrency = (value) => {
    const num = Number(value) || 0;
    if (num >= 1000000) return `₺${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `₺${(num / 1000).toFixed(1)}K`;
    return `₺${Math.round(num).toLocaleString('tr-TR')}`;
  };

  const formatNumber = (value) => {
    const num = Number(value) || 0;
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return Math.round(num).toLocaleString('tr-TR');
  };

  // -------------------------------------------------------------------------
  // DATA FETCHING - Using SAME endpoints as Overview2
  // -------------------------------------------------------------------------

  // Fetch brand logo
  const fetchBrandLogo = async () => {
    try {
      const brandId = getBrandId();
      if (!brandId) return;
      
      const response = await fetch(`https://api.glowytics.com/api/brand-info/${brandId}/details`, {
        credentials: 'include',
        headers: { 'X-Brand-ID': brandId }
      });
      
      if (response.ok) {
        const data = await response.json();
        setLogoUrl(data?.logoUrl || '/logoicon.png');
      }
    } catch (error) {
      console.error('Error fetching logo:', error);
    }
  };

  // Fetch Google Ads unified data
  const fetchGoogleAdsData = async () => {
    try {
      const brandId = getBrandId();
      if (!brandId) return;
      
      setIsLoadingGoogle(true);
      
      const url = `https://api.glowytics.com/api/google-ads/unified-googleads-ga4-performance?dateRange=${dateRange}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setGoogleAdsData(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching Google Ads data:', error);
    } finally {
      setIsLoadingGoogle(false);
    }
  };

  // Fetch Meta Ads unified data
  const fetchMetaAdsData = async () => {
    try {
      const brandId = getBrandId();
      if (!brandId) return;
      
      setIsLoadingMeta(true);
      
      const url = `https://api.glowytics.com/api/google-ads/unified-meta-ga4-performance?dateRange=${dateRange}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setMetaAdsData(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching Meta Ads data:', error);
    } finally {
      setIsLoadingMeta(false);
    }
  };

  // Fetch GA Traffic Source data
  const fetchTrafficData = async () => {
    try {
      const brandId = getBrandId();
      if (!brandId) return;
      
      setIsLoadingTraffic(true);
      
      const url = `https://api.glowytics.com/api/google-ads/ga-traffic-source?dateRange=${dateRange}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setTrafficData(data);
      }
    } catch (error) {
      console.error('Error fetching traffic data:', error);
    } finally {
      setIsLoadingTraffic(false);
    }
  };

  // Fetch Live Today data
  const fetchLiveData = async () => {
    setLoadingLive(true);
    const brandId = getBrandId();
    if (!brandId) {
      setLoadingLive(false);
      return;
    }

    const headers = { 'X-Brand-ID': brandId };

    try {
      const [gadsRes, metaRes, ga4Res] = await Promise.allSettled([
        fetch(`https://api.glowytics.com/api/brands/${brandId}/live/google-ads/today`, 
          { credentials: 'include', headers }),
        fetch(`https://api.glowytics.com/api/brands/${brandId}/live/meta-ads/today`, 
          { credentials: 'include', headers }),
        fetch(`https://api.glowytics.com/api/brands/${brandId}/live/ga4/today`, 
          { credentials: 'include', headers })
      ]);

      if (gadsRes.status === 'fulfilled' && gadsRes.value.ok) {
        setLiveGads(await gadsRes.value.json());
      }
      if (metaRes.status === 'fulfilled' && metaRes.value.ok) {
        setLiveMeta(await metaRes.value.json());
      }
      if (ga4Res.status === 'fulfilled' && ga4Res.value.ok) {
        setLiveGa4(await ga4Res.value.json());
      }
    } catch (error) {
      console.error('Error fetching live data:', error);
    } finally {
      setLoadingLive(false);
    }
  };

  // -------------------------------------------------------------------------
  // CALCULATED METRICS
  // -------------------------------------------------------------------------

  const googleTotals = useMemo(() => {
    return googleAdsData.reduce((acc, day) => ({
      cost: acc.cost + (Number(day.cost) || 0),
      clicks: acc.clicks + (Number(day.clicks) || 0),
      impressions: acc.impressions + (Number(day.impressions) || 0),
      conversions: acc.conversions + (Number(day.conversions) || 0),
      conversionValue: acc.conversionValue + (Number(day.conversion_value) || 0),
      ga4Revenue: acc.ga4Revenue + (Number(day.ga4_total_revenue) || 0),
      ga4Sessions: acc.ga4Sessions + (Number(day.ga4_sessions) || 0)
    }), { cost: 0, clicks: 0, impressions: 0, conversions: 0, conversionValue: 0, ga4Revenue: 0, ga4Sessions: 0 });
  }, [googleAdsData]);

  const metaTotals = useMemo(() => {
    return metaAdsData.reduce((acc, day) => ({
      spend: acc.spend + (Number(day.spend) || 0),
      clicks: acc.clicks + (Number(day.clicks) || 0),
      impressions: acc.impressions + (Number(day.impressions) || 0),
      conversions: acc.conversions + (Number(day.conversions) || Number(day.purchases) || 0),
      conversionValue: acc.conversionValue + (Number(day.conversion_value) || Number(day.purchase_value) || 0),
      ga4Revenue: acc.ga4Revenue + (Number(day.ga4_total_revenue) || 0),
      ga4Sessions: acc.ga4Sessions + (Number(day.ga4_sessions) || 0)
    }), { spend: 0, clicks: 0, impressions: 0, conversions: 0, conversionValue: 0, ga4Revenue: 0, ga4Sessions: 0 });
  }, [metaAdsData]);

  const totalMetrics = useMemo(() => {
    const totalSpend = googleTotals.cost + metaTotals.spend;
    const totalRevenue = googleTotals.ga4Revenue + metaTotals.ga4Revenue;
    const totalConversions = googleTotals.conversions + metaTotals.conversions;
    const totalSessions = googleTotals.ga4Sessions + metaTotals.ga4Sessions;
    const roas = totalSpend > 0 ? totalRevenue / totalSpend : 0;
    
    return {
      spend: totalSpend,
      revenue: totalRevenue,
      conversions: totalConversions,
      sessions: totalSessions,
      roas: roas,
      cpc: (googleTotals.clicks + metaTotals.clicks) > 0 
        ? totalSpend / (googleTotals.clicks + metaTotals.clicks) 
        : 0
    };
  }, [googleTotals, metaTotals]);

  // Traffic sources breakdown
  const trafficSources = useMemo(() => {
    if (!trafficData?.detailedData) return [];
    
    const sourceMap = {};
    trafficData.detailedData.forEach(row => {
      const source = row.source || 'direct';
      if (!sourceMap[source]) {
        sourceMap[source] = { sessions: 0, revenue: 0 };
      }
      sourceMap[source].sessions += Number(row.sessions) || 0;
      sourceMap[source].revenue += Number(row.totalRevenue) || 0;
    });
    
    const total = Object.values(sourceMap).reduce((sum, s) => sum + s.sessions, 0);
    
    return Object.entries(sourceMap)
      .map(([name, data]) => ({
        name,
        sessions: data.sessions,
        revenue: data.revenue,
        percentage: total > 0 ? (data.sessions / total) * 100 : 0
      }))
      .sort((a, b) => b.sessions - a.sessions)
      .slice(0, 6);
  }, [trafficData]);

  // Chart data
  const chartData = useMemo(() => {
    const allDates = [...new Set([
      ...googleAdsData.map(d => d.date),
      ...metaAdsData.map(d => d.date)
    ])].sort();

    const labels = allDates.map(d => {
      const date = new Date(d);
      return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' });
    });

    const revenueData = allDates.map(date => {
      const gDay = googleAdsData.find(d => d.date === date);
      const mDay = metaAdsData.find(d => d.date === date);
      return (Number(gDay?.ga4_total_revenue) || 0) + (Number(mDay?.ga4_total_revenue) || 0);
    });

    const spendData = allDates.map(date => {
      const gDay = googleAdsData.find(d => d.date === date);
      const mDay = metaAdsData.find(d => d.date === date);
      return (Number(gDay?.cost) || 0) + (Number(mDay?.spend) || 0);
    });

    return {
      labels,
      datasets: [
        {
          label: 'Revenue',
          data: revenueData,
          borderColor: '#10B981',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          fill: true,
          tension: 0.4,
          yAxisID: 'y'
        },
        {
          label: 'Ad Spend',
          data: spendData,
          borderColor: '#F59E0B',
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          fill: true,
          tension: 0.4,
          yAxisID: 'y'
        }
      ]
    };
  }, [googleAdsData, metaAdsData]);

  // -------------------------------------------------------------------------
  // EFFECTS
  // -------------------------------------------------------------------------

  useEffect(() => {
    fetchBrandLogo();
    fetchLiveData();
  }, []);

  useEffect(() => {
    const fetchAll = async () => {
      setIsLoading(true);
      await Promise.all([
        fetchGoogleAdsData(),
        fetchMetaAdsData(),
        fetchTrafficData()
      ]);
      setIsLoading(false);
    };
    fetchAll();
  }, [dateRange]);

  // -------------------------------------------------------------------------
  // RENDER
  // -------------------------------------------------------------------------

  const liveRevenue = (Number(liveGads?.conversion_value) || 0) + (Number(liveMeta?.conversion_value) || 0);
  const liveSpend = (Number(liveGads?.cost) || 0) + (Number(liveMeta?.spend) || 0);
  const liveSessions = Number(liveGa4?.sessions) || 0;
  const liveOrders = (Number(liveGads?.conversions) || 0) + (Number(liveMeta?.conversions) || 0);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* ================================================================== */}
      {/* HEADER */}
      {/* ================================================================== */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {logoUrl && (
              <img src={logoUrl} alt="Brand" className="w-12 h-12 rounded-xl object-contain bg-gray-50 p-1" />
            )}
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
              <p className="text-sm text-gray-500">Performance Overview</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {/* Date Range Selector */}
            <div className="flex items-center bg-gray-100 rounded-xl p-1">
              {[
                { key: 'last7', label: '7 Days' },
                { key: 'last30', label: '30 Days' },
                { key: 'last90', label: '90 Days' }
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setDateRange(key)}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                    dateRange === key
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            <button
              onClick={() => {
                fetchGoogleAdsData();
                fetchMetaAdsData();
                fetchTrafficData();
                fetchLiveData();
              }}
              className="p-2.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-colors"
            >
              <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* ================================================================== */}
      {/* LIVE TODAY BANNER */}
      {/* ================================================================== */}
      <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-700 rounded-2xl p-6 mb-6 text-white shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse" />
            <span className="font-semibold text-lg">Live Today</span>
          </div>
          {loadingLive && <Loader2 className="w-5 h-5 animate-spin" />}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="bg-white/10 backdrop-blur rounded-xl p-4">
            <p className="text-blue-200 text-sm mb-1">Revenue</p>
            <p className="text-3xl font-bold">{formatCurrency(liveRevenue)}</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-4">
            <p className="text-blue-200 text-sm mb-1">Ad Spend</p>
            <p className="text-3xl font-bold">{formatCurrency(liveSpend)}</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-4">
            <p className="text-blue-200 text-sm mb-1">Sessions</p>
            <p className="text-3xl font-bold">{formatNumber(liveSessions)}</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-4">
            <p className="text-blue-200 text-sm mb-1">Orders</p>
            <p className="text-3xl font-bold">{formatNumber(liveOrders)}</p>
          </div>
        </div>
      </div>

      {/* ================================================================== */}
      {/* MAIN KPIs */}
      {/* ================================================================== */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
        {/* Total Revenue */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-500">Total Revenue</span>
            <div className="p-2 bg-emerald-100 rounded-lg">
              <DollarSign className="w-4 h-4 text-emerald-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {isLoading ? '...' : formatCurrency(totalMetrics.revenue)}
          </p>
        </div>

        {/* Ad Spend */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-500">Ad Spend</span>
            <div className="p-2 bg-amber-100 rounded-lg">
              <Zap className="w-4 h-4 text-amber-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {isLoading ? '...' : formatCurrency(totalMetrics.spend)}
          </p>
        </div>

        {/* ROAS */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-500">ROAS</span>
            <div className="p-2 bg-violet-100 rounded-lg">
              <Target className="w-4 h-4 text-violet-600" />
            </div>
          </div>
          <p className={`text-2xl font-bold ${
            totalMetrics.roas >= 3 ? 'text-emerald-600' : 
            totalMetrics.roas >= 1 ? 'text-amber-600' : 'text-rose-600'
          }`}>
            {isLoading ? '...' : `${totalMetrics.roas.toFixed(2)}x`}
          </p>
        </div>

        {/* Conversions */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-500">Orders</span>
            <div className="p-2 bg-blue-100 rounded-lg">
              <ShoppingCart className="w-4 h-4 text-blue-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {isLoading ? '...' : formatNumber(totalMetrics.conversions)}
          </p>
        </div>

        {/* Sessions */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-500">Sessions</span>
            <div className="p-2 bg-cyan-100 rounded-lg">
              <Users className="w-4 h-4 text-cyan-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {isLoading ? '...' : formatNumber(totalMetrics.sessions)}
          </p>
        </div>

        {/* CPC */}
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-500">Avg. CPC</span>
            <div className="p-2 bg-rose-100 rounded-lg">
              <MousePointerClick className="w-4 h-4 text-rose-600" />
            </div>
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {isLoading ? '...' : formatCurrency(totalMetrics.cpc)}
          </p>
        </div>
      </div>

      {/* ================================================================== */}
      {/* CHART + PLATFORM BREAKDOWN */}
      {/* ================================================================== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Revenue & Spend Chart */}
        <div className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Revenue vs Ad Spend</h2>
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-emerald-500" />
                <span className="text-gray-600">Revenue</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-amber-500" />
                <span className="text-gray-600">Ad Spend</span>
              </div>
            </div>
          </div>
          <div className="h-80">
            {isLoading ? (
              <div className="h-full flex items-center justify-center">
                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
              </div>
            ) : (
              <LineChart
                data={chartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  interaction: { intersect: false, mode: 'index' },
                  plugins: {
                    legend: { display: false },
                    tooltip: {
                      backgroundColor: 'white',
                      titleColor: '#1f2937',
                      bodyColor: '#6b7280',
                      borderColor: '#e5e7eb',
                      borderWidth: 1,
                      padding: 12,
                      callbacks: {
                        label: (ctx) => `${ctx.dataset.label}: ${formatCurrency(ctx.raw)}`
                      }
                    }
                  },
                  scales: {
                    x: { grid: { display: false }, ticks: { color: '#9ca3af' } },
                    y: {
                      grid: { color: '#f3f4f6' },
                      ticks: { color: '#9ca3af', callback: (v) => formatCurrency(v) }
                    }
                  }
                }}
              />
            )}
          </div>
        </div>

        {/* Traffic Sources */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">Traffic Sources</h2>
          {isLoadingTraffic ? (
            <div className="h-64 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
          ) : trafficSources.length > 0 ? (
            <div className="space-y-4">
              {trafficSources.map((source, idx) => (
                <div key={idx}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 capitalize">{source.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">{formatNumber(source.sessions)}</span>
                      <span className="text-xs text-gray-400">({source.percentage.toFixed(1)}%)</span>
                    </div>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${Math.min(source.percentage, 100)}%`,
                        backgroundColor: ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'][idx % 6]
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-400">
              <p>No traffic data available</p>
            </div>
          )}
        </div>
      </div>

      {/* ================================================================== */}
      {/* PLATFORM CARDS */}
      {/* ================================================================== */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Google Ads */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-blue-100 rounded-xl">
              <Search className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Google Ads</h3>
              <p className="text-sm text-gray-500">Search & Display Performance</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-1">Spend</p>
              <p className="text-xl font-bold text-gray-900">{formatCurrency(googleTotals.cost)}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-1">Revenue</p>
              <p className="text-xl font-bold text-emerald-600">{formatCurrency(googleTotals.ga4Revenue)}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-1">ROAS</p>
              <p className="text-xl font-bold text-violet-600">
                {googleTotals.cost > 0 ? (googleTotals.ga4Revenue / googleTotals.cost).toFixed(2) : '0.00'}x
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-1">Conversions</p>
              <p className="text-xl font-bold text-gray-900">{formatNumber(googleTotals.conversions)}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-1">Clicks</p>
              <p className="text-xl font-bold text-gray-900">{formatNumber(googleTotals.clicks)}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-1">Impressions</p>
              <p className="text-xl font-bold text-gray-900">{formatNumber(googleTotals.impressions)}</p>
            </div>
          </div>
        </div>

        {/* Meta Ads */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-indigo-100 rounded-xl">
              <Globe className="w-6 h-6 text-indigo-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Meta Ads</h3>
              <p className="text-sm text-gray-500">Facebook & Instagram Performance</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-1">Spend</p>
              <p className="text-xl font-bold text-gray-900">{formatCurrency(metaTotals.spend)}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-1">Revenue</p>
              <p className="text-xl font-bold text-emerald-600">{formatCurrency(metaTotals.ga4Revenue)}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-1">ROAS</p>
              <p className="text-xl font-bold text-violet-600">
                {metaTotals.spend > 0 ? (metaTotals.ga4Revenue / metaTotals.spend).toFixed(2) : '0.00'}x
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-1">Conversions</p>
              <p className="text-xl font-bold text-gray-900">{formatNumber(metaTotals.conversions)}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-1">Clicks</p>
              <p className="text-xl font-bold text-gray-900">{formatNumber(metaTotals.clicks)}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-1">Impressions</p>
              <p className="text-xl font-bold text-gray-900">{formatNumber(metaTotals.impressions)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* ================================================================== */}
      {/* DAILY BREAKDOWN TABLE */}
      {/* ================================================================== */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">Daily Performance</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Date</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Revenue</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Ad Spend</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">ROAS</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Sessions</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Orders</th>
              </tr>
            </thead>
            <tbody>
              {[...new Set([...googleAdsData.map(d => d.date), ...metaAdsData.map(d => d.date)])]
                .sort()
                .reverse()
                .slice(0, 10)
                .map(date => {
                  const gDay = googleAdsData.find(d => d.date === date) || {};
                  const mDay = metaAdsData.find(d => d.date === date) || {};
                  const revenue = (Number(gDay.ga4_total_revenue) || 0) + (Number(mDay.ga4_total_revenue) || 0);
                  const spend = (Number(gDay.cost) || 0) + (Number(mDay.spend) || 0);
                  const sessions = (Number(gDay.ga4_sessions) || 0) + (Number(mDay.ga4_sessions) || 0);
                  const orders = (Number(gDay.conversions) || 0) + (Number(mDay.conversions) || 0);
                  const roas = spend > 0 ? revenue / spend : 0;

                  return (
                    <tr key={date} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                      <td className="py-4 px-4 text-sm font-medium text-gray-900">
                        {new Date(date).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      </td>
                      <td className="py-4 px-4 text-sm text-right font-semibold text-emerald-600">
                        {formatCurrency(revenue)}
                      </td>
                      <td className="py-4 px-4 text-sm text-right text-gray-600">
                        {formatCurrency(spend)}
                      </td>
                      <td className="py-4 px-4 text-sm text-right">
                        <span className={`font-semibold ${
                          roas >= 3 ? 'text-emerald-600' : roas >= 1 ? 'text-amber-600' : 'text-rose-600'
                        }`}>
                          {roas.toFixed(2)}x
                        </span>
                      </td>
                      <td className="py-4 px-4 text-sm text-right text-gray-600">
                        {formatNumber(sessions)}
                      </td>
                      <td className="py-4 px-4 text-sm text-right text-gray-600">
                        {formatNumber(orders)}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
